/**
 * Theme + full client customization (Spec 11). Manages light/dark mode and five
 * color ROLES the client approved:
 *   Primary (--accent) · Secondary (--accent-2) · Headings (--text) ·
 *   Paragraph (--body) · Numbers (--num).
 * Headings/Paragraph/Numbers default to "auto" (follow the theme); a custom
 * value overrides. Everything persists to localStorage and is written onto
 * <html> so all CSS-variable tokens swap instantly. index.html applies saved
 * values before React paints to avoid a flash.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";

export type ThemeMode = "dark" | "light";
export type ColorRole = "accent" | "accent2" | "text" | "body" | "num";

interface ThemeCtx {
  mode: ThemeMode;
  toggle: () => void;
  setMode: (m: ThemeMode) => void;
  colors: Record<ColorRole, string>; // value or "auto" (for text/body/num)
  setColor: (role: ColorRole, value: string) => void;
  useBrandColors: () => void;
}

const Ctx = createContext<ThemeCtx | null>(null);

const KEY = {
  mode: "dd-theme",
  accent: "dd-accent",
  accent2: "dd-accent2",
  text: "dd-text",
  body: "dd-body",
  num: "dd-num",
} as const;
const DEFAULTS = {
  accent: "#15b8a6",
  accent2: "#6c8ae4",
  text: "auto",
  body: "auto",
  num: "auto",
} as const;
// The CSS custom-property each role drives.
const VAR = {
  accent: "--accent",
  accent2: "--accent-2",
  text: "--text",
  body: "--body",
  num: "--num",
} as const;

function read(role: ColorRole): string {
  if (typeof localStorage === "undefined") return DEFAULTS[role];
  return localStorage.getItem(KEY[role]) ?? DEFAULTS[role];
}
function luminance(hex: string): number {
  const c = hex.replace("#", "");
  if (c.length < 6) return 1;
  const r = parseInt(c.slice(0, 2), 16),
    g = parseInt(c.slice(2, 4), 16),
    b = parseInt(c.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

function applyColor(role: ColorRole, value: string): void {
  const el = document.documentElement;
  if (value === "auto") {
    el.style.removeProperty(VAR[role]);
    if (role === "accent") el.style.removeProperty("--accent-contrast");
    return;
  }
  el.style.setProperty(VAR[role], value);
  if (role === "accent") {
    el.style.setProperty("--accent-contrast", luminance(value) > 0.62 ? "#10131a" : "#ffffff");
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [mode, setModeState] = useState<ThemeMode>(() =>
    typeof localStorage !== "undefined" && localStorage.getItem(KEY.mode) === "light" ? "light" : "dark",
  );
  const [colors, setColors] = useState<Record<ColorRole, string>>(() => ({
    accent: read("accent"),
    accent2: read("accent2"),
    text: read("text"),
    body: read("body"),
    num: read("num"),
  }));

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    localStorage.setItem(KEY.mode, mode);
  }, [mode]);

  useEffect(() => {
    (Object.keys(colors) as ColorRole[]).forEach((role) => {
      applyColor(role, colors[role]);
      localStorage.setItem(KEY[role], colors[role]);
    });
  }, [colors]);

  const setColor = useCallback((role: ColorRole, value: string): void =>
    setColors((c) => ({ ...c, [role]: value })), []);

  return (
    <Ctx.Provider
      value={{
        mode,
        toggle: () => setModeState((m) => (m === "dark" ? "light" : "dark")),
        setMode: setModeState,
        colors,
        setColor,
        // Real onboarding will read the client's site; here a sensible default.
        useBrandColors: () => setColors((c) => ({ ...c, accent: "#1f7a5a", accent2: "#d8a341" })),
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme must be used inside ThemeProvider");
  return v;
}

/** Small sun/moon toggle for the top nav. */
export function ThemeToggle(): React.ReactElement {
  const { mode, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      title={mode === "dark" ? "Switch to light" : "Switch to dark"}
      aria-label="Toggle light or dark mode"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 34,
        height: 34,
        borderRadius: "var(--r-sm)",
        border: "1px solid var(--border)",
        background: "var(--surface-2)",
        color: "var(--body)",
        cursor: "pointer",
        fontSize: 15,
      }}
    >
      {mode === "dark" ? "☀" : "☾"}
    </button>
  );
}
